import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TableRowsIcon from '@mui/icons-material/TableRows';
import PieChartIcon from '@mui/icons-material/PieChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import { apiService } from '../services/apiService';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

type BillingFilter = '' | 'MONTHLY' | 'YEARLY';
type ViewMode = 'list' | 'pie' | 'bar';

const CHART_COLORS = [
  'rgba(25, 118, 210, 0.85)',
  'rgba(156, 39, 176, 0.85)',
  'rgba(46, 125, 50, 0.85)',
  'rgba(237, 108, 2, 0.85)',
  'rgba(211, 47, 47, 0.85)',
  'rgba(0, 150, 136, 0.85)',
  'rgba(63, 81, 181, 0.85)',
  'rgba(121, 85, 72, 0.85)',
  'rgba(0, 172, 193, 0.85)',
  'rgba(255, 143, 0, 0.85)',
];

const TOP_N = 10;

function buildRevenueByTenant(rows: any[]): { labels: string[]; values: number[] } {
  const sorted = [...rows]
    .map((r) => ({
      label: String(r.name || r.slug || `Tenant ${r.tenantId}`).slice(0, 28),
      value: Math.max(0, Number(r.estimatedRecurringUsd) || 0),
    }))
    .sort((a, b) => b.value - a.value);

  const positive = sorted.filter((x) => x.value > 0);
  if (positive.length === 0) {
    return { labels: [], values: [] };
  }
  if (positive.length <= TOP_N) {
    return {
      labels: positive.map((x) => x.label),
      values: positive.map((x) => x.value),
    };
  }
  const top = positive.slice(0, TOP_N);
  const other = positive.slice(TOP_N).reduce((s, x) => s + x.value, 0);
  return {
    labels: [...top.map((x) => x.label), `Other (${positive.length - TOP_N} tenants)`],
    values: [...top.map((x) => x.value), other],
  };
}

function buildTenantsByPlan(rows: any[]): { labels: string[]; values: number[] } {
  const map = new Map<string, number>();
  for (const r of rows) {
    const p = r.planType || 'UNKNOWN';
    map.set(p, (map.get(p) || 0) + 1);
  }
  const entries = Array.from(map).sort((a, b) => b[1] - a[1]);
  return {
    labels: entries.map(([k]) => k),
    values: entries.map(([, v]) => v),
  };
}

const SuperAdminRevenuePage: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [billingCycle, setBillingCycle] = useState<BillingFilter>('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  const query = useMemo(
    () => ({
      search: search.trim() || undefined,
      ...(billingCycle ? { billingCycle } : {}),
    }),
    [search, billingCycle]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getAdminRevenueTenants(query);
      setRows(data || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load revenue');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = useMemo(() => {
    let monthly = 0;
    let yearly = 0;
    for (const r of rows) {
      const usd = Number(r.estimatedRecurringUsd) || 0;
      if (r.recurringPeriod === 'MONTHLY') monthly += usd;
      else if (r.recurringPeriod === 'YEARLY') yearly += usd;
    }
    return { monthly, yearly, all: monthly + yearly };
  }, [rows]);

  const formatMoney = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

  const chartSeries = useMemo(() => {
    const byTenant = buildRevenueByTenant(rows);
    if (byTenant.values.length > 0) {
      return {
        kind: 'revenue' as const,
        labels: byTenant.labels,
        values: byTenant.values,
        yTitle: 'Est. recurring (USD)',
      };
    }
    const byPlan = buildTenantsByPlan(rows);
    return {
      kind: 'count' as const,
      labels: byPlan.labels,
      values: byPlan.values,
      yTitle: 'Tenant count',
    };
  }, [rows]);

  const pieData = useMemo(
    () => ({
      labels: chartSeries.labels,
      datasets: [
        {
          data: chartSeries.values,
          backgroundColor: chartSeries.labels.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
          borderColor: '#fff',
          borderWidth: 1,
        },
      ],
    }),
    [chartSeries]
  );

  const barData = useMemo(
    () => ({
      labels: chartSeries.labels,
      datasets: [
        {
          label: chartSeries.yTitle,
          data: chartSeries.values,
          backgroundColor: chartSeries.labels.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
          borderRadius: 6,
        },
      ],
    }),
    [chartSeries]
  );

  const barOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx: any) => {
              const v = ctx.parsed.y ?? ctx.parsed;
              if (chartSeries.kind === 'revenue') {
                return formatMoney(Number(v));
              }
              return `${v} tenant(s)`;
            },
          },
        },
      },
      scales: {
        x: {
          ticks: { maxRotation: 45, minRotation: 0, autoSkip: true, maxTicksLimit: 12 },
        },
        y: { beginAtZero: true },
      },
    }),
    [chartSeries.kind]
  );

  const pieOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'right' as const },
        tooltip: {
          callbacks: {
            label: (ctx: any) => {
              const raw = ctx.raw as number;
              if (chartSeries.kind === 'revenue') {
                const total = chartSeries.values.reduce((a, b) => a + b, 0) || 1;
                const pct = ((raw / total) * 100).toFixed(1);
                return `${formatMoney(raw)} (${pct}%)`;
              }
              const total = chartSeries.values.reduce((a, b) => a + b, 0) || 1;
              const pct = ((raw / total) * 100).toFixed(1);
              return `${raw} tenants (${pct}%)`;
            },
          },
        },
      },
    }),
    [chartSeries]
  );

  const handleViewChange = (_: React.MouseEvent<HTMLElement>, next: ViewMode | null) => {
    if (next != null) setViewMode(next);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <AttachMoneyIcon />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Revenue
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            label="Search tenant"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="billing-filter-label">Billing cycle</InputLabel>
            <Select
              labelId="billing-filter-label"
              label="Billing cycle"
              value={billingCycle}
              onChange={(e) => setBillingCycle(e.target.value as BillingFilter)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="MONTHLY">Monthly</MenuItem>
              <MenuItem value="YEARLY">Yearly</MenuItem>
            </Select>
          </FormControl>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewChange}
            size="small"
            aria-label="Revenue view"
          >
            <ToggleButton value="list" aria-label="List">
              <TableRowsIcon sx={{ mr: 0.5, fontSize: 20 }} />
              List
            </ToggleButton>
            <ToggleButton value="pie" aria-label="Pie chart">
              <PieChartIcon sx={{ mr: 0.5, fontSize: 20 }} />
              Pie chart
            </ToggleButton>
            <ToggleButton value="bar" aria-label="Bar graph">
              <BarChartIcon sx={{ mr: 0.5, fontSize: 20 }} />
              Graph
            </ToggleButton>
          </ToggleButtonGroup>
          <Button variant="outlined" onClick={load} disabled={loading}>
            Reload
          </Button>
        </Box>
        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            Estimated recurring (monthly tenants / MRR slice):{' '}
            <strong>{formatMoney(totals.monthly)}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Estimated recurring (yearly tenants / per-year):{' '}
            <strong>{formatMoney(totals.yearly)}</strong>
          </Typography>
          {billingCycle === '' && (
            <Typography variant="body2" color="text.secondary">
              Sum of both (not annualized): <strong>{formatMoney(totals.all)}</strong>
            </Typography>
          )}
        </Box>
        {viewMode !== 'list' && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {chartSeries.kind === 'revenue'
              ? 'Pie chart / graph show estimated recurring revenue by tenant (top ' + TOP_N + ', rest grouped as Other).'
              : 'No recurring amounts in this filter; charts show tenant count by plan.'}
          </Typography>
        )}
      </Paper>

      {loading && <CircularProgress size={22} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {viewMode === 'list' && (
        <Paper sx={{ p: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tenant ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Billing</TableCell>
                <TableCell>Licenses</TableCell>
                <TableCell>Est. recurring</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Last payment</TableCell>
                <TableCell>Last paid at</TableCell>
                <TableCell>Stripe customer</TableCell>
                <TableCell>Stripe subscription</TableCell>
                <TableCell>Invoice id</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.tenantId}>
                  <TableCell>{r.tenantId}</TableCell>
                  <TableCell>{r.name || '-'}</TableCell>
                  <TableCell>{r.slug || '-'}</TableCell>
                  <TableCell>{r.planType || '-'}</TableCell>
                  <TableCell>{r.status || '-'}</TableCell>
                  <TableCell>{r.billingCycle || '-'}</TableCell>
                  <TableCell>{r.licenseCount ?? '-'}</TableCell>
                  <TableCell>
                    {r.estimatedRecurringUsd != null ? formatMoney(Number(r.estimatedRecurringUsd)) : '-'}
                  </TableCell>
                  <TableCell>{r.recurringPeriod || '-'}</TableCell>
                  <TableCell>
                    {r.lastPaymentAmountUsd != null ? formatMoney(Number(r.lastPaymentAmountUsd)) : '-'}
                  </TableCell>
                  <TableCell>{r.lastPaymentAt || '-'}</TableCell>
                  <TableCell sx={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.stripeCustomerId || '-'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.stripeSubscriptionId || '-'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.lastPaymentStripeInvoiceId || '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {viewMode === 'pie' && (
        <Paper sx={{ p: 2 }}>
          {rows.length === 0 && !loading ? (
            <Typography color="text.secondary">No data for this filter.</Typography>
          ) : (
            <Box sx={{ height: 420, maxWidth: 900, mx: 'auto' }}>
              <Pie data={pieData} options={pieOptions} />
            </Box>
          )}
        </Paper>
      )}

      {viewMode === 'bar' && (
        <Paper sx={{ p: 2 }}>
          {rows.length === 0 && !loading ? (
            <Typography color="text.secondary">No data for this filter.</Typography>
          ) : (
            <Box sx={{ height: 420, width: '100%' }}>
              <Bar data={barData} options={barOptions} />
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default SuperAdminRevenuePage;
